/**
 * Grafico del risultato BI. Riceve columns+data (già "shaped") e il `type` scelto.
 * Non decide il tipo (lo fa il genitore con l'euristica): qui solo il rendering.
 *
 * - 'table'  -> null (la tabella la mostra ResultTable).
 * - 'number' -> "numero grande" (Statistic) se c'è UN'unica cella numerica; else null.
 * - 'bar'    -> BarChart: X = etichetta (ragione_sociale / nome+cognome / prima non
 *              numerica), Y = prima colonna numerica.
 * - 'line'   -> LineChart, stessa struttura del bar.
 *
 * isNumericColumn è importata da chartHeuristic (fonte unica: esclude gli *_id).
 * Casi limite: etichetta vuota -> "(vuoto)"; max 30 categorie (con nota
 * "mostrate 30 di N"); se manca la coppia categoria+numero -> null.
 */

import { Statistic } from 'antd'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { isNumericColumn } from '../chartHeuristic'
import type { ChartType } from '../chartHeuristic'

type Row = Record<string, unknown>

const MAX_CATEGORIES = 30
const SERIES_COLOR = '#1677ff'
const CHART_MARGIN = { top: 10, right: 10, left: 0, bottom: 60 }
// Campo calcolato per l'asse X (nome improbabile da collidere con una colonna reale).
const LABEL_KEY = '__label'

interface ResultChartProps {
  columns: string[]
  data: Row[]
  type: ChartType
}

/** Valore reso stringa "pulita": null/undefined/'' -> ''. */
function asLabel(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim()
}

function ResultChart({ columns, data, type }: ResultChartProps) {
  if (type === 'table') return null

  const numericCols = columns.filter((c) => isNumericColumn(data, c))

  // ── numero grande ──────────────────────────────────────────────────────────
  if (type === 'number') {
    if (data.length === 1 && numericCols.length === 1) {
      const v = data[0][numericCols[0]]
      if (typeof v === 'number' && Number.isFinite(v)) {
        return <Statistic value={v} groupSeparator="." decimalSeparator="," />
      }
    }
    return null
  }

  // ── bar / line: Y = prima numerica; X = etichetta calcolata ──────────────────
  const numKey = numericCols[0]
  const fallbackCatKey = columns.find((c) => !isNumericColumn(data, c))
  if (!numKey || !fallbackCatKey) return null

  // Etichetta della categoria per una riga:
  //  ragione_sociale (se valorizzata) -> nome+cognome -> prima colonna non numerica -> '(vuoto)'.
  const buildLabel = (r: Row): string => {
    if (columns.includes('ragione_sociale')) {
      const rs = asLabel(r['ragione_sociale'])
      if (rs) return rs
    }
    if (columns.includes('nome') || columns.includes('cognome')) {
      const parts = [asLabel(r['nome']), asLabel(r['cognome'])].filter((s) => s !== '')
      if (parts.length) return parts.join(' ')
    }
    const fb = asLabel(r[fallbackCatKey])
    return fb || '(vuoto)'
  }

  const chartData = data.slice(0, MAX_CATEGORIES).map((r) => ({
    ...r,
    [LABEL_KEY]: buildLabel(r),
  }))
  const truncatedNote =
    data.length > MAX_CATEGORIES ? `mostrate ${MAX_CATEGORIES} di ${data.length}` : null

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        {type === 'bar' ? (
          <BarChart data={chartData} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={LABEL_KEY} angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
            <YAxis />
            <RTooltip />
            <Bar dataKey={numKey} fill={SERIES_COLOR} name={numKey} />
          </BarChart>
        ) : (
          <LineChart data={chartData} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={LABEL_KEY} angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
            <YAxis />
            <RTooltip />
            <Line type="monotone" dataKey={numKey} stroke={SERIES_COLOR} dot={{ r: 2 }} name={numKey} />
          </LineChart>
        )}
      </ResponsiveContainer>
      {truncatedNote && (
        <div style={{ textAlign: 'center', fontSize: 12, color: '#888', marginTop: 4 }}>
          {truncatedNote}
        </div>
      )}
    </div>
  )
}

export default ResultChart
